import type { AppMode } from '@iheartjane/types';
import { createExternalStore, Storage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

type LegacyCartUuidRead = (
  appMode: AppMode,
  storeId?: string | number
) => string | null;

type LegacyCartUuidClear = (
  appMode: AppMode,
  storeId?: string | number
) => void;

type CartUuidStorageKey = (
  appMode: AppMode,
  storeId?: string | number
) => string;

/**
 * App-mode-scoped only. Kiosk depends on this default — `cart-uuid-kiosk` has
 * live writers — so a consumer needing store scoping injects `storageKey`.
 */
const defaultCartUuidStorageKey: CartUuidStorageKey = (appMode) =>
  `cart-uuid-${appMode}`;

/** Owns the client-side cart uuid creation (non-redux) */
export const createLocalCartUuidStore = ({
  legacyClear,
  legacyRead,
  storageKey: cartUuidStorageKey = defaultCartUuidStorageKey,
}: {
  legacyClear?: LegacyCartUuidClear;
  legacyRead?: LegacyCartUuidRead;
  storageKey?: CartUuidStorageKey;
} = {}) => {
  const { notify, subscribe } = createExternalStore();

  const getLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number
  ): string | null =>
    Storage.get<string>(cartUuidStorageKey(appMode, storeId)) ||
    legacyRead?.(appMode, storeId) ||
    null;

  /** `storeId` mirrors the getter's position so a scoped key round-trips. */
  const updateLocalCartUuid = (
    appMode: AppMode,
    storeId: string | number | undefined,
    cartUuid: string | null
  ) => {
    const key = cartUuidStorageKey(appMode, storeId);

    if (cartUuid) {
      Storage.set(key, cartUuid);
    } else {
      Storage.remove(key);
      // Clears only, because a read falls back to the legacy source whenever
      // this key is absent — so a clear that left it behind would be undone on
      // the next read. A *write* needs no clear: the read prefers this key, so
      // the legacy value is already inert, and consumers may still need it as a
      // flag-off fallback if the cart flag is switched back off.
      // TODO(carts-v3): remove once rolled out
      legacyClear?.(appMode, storeId);
    }

    notify();
  };

  /**
   * Subscribed read to ensure it logs the uuid when one is created. `enabled`
   * gates the read, not the hook: `getSnapshot` hits localStorage on every call.
   */
  const useLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number,
    enabled = true
  ) =>
    useSyncExternalStore(
      subscribe,
      () => (enabled ? getLocalCartUuid(appMode, storeId) : null),
      () => null
    );

  return { getLocalCartUuid, updateLocalCartUuid, useLocalCartUuid };
};
